import {useFocusZone} from '@primer/react'
import {FocusKeys} from '@primer/behaviors'
// eslint-disable-next-line no-restricted-imports
import {iterateFocusableElements} from '@primer/behaviors/utils'

type RelativeElementPosition = 'first' | 'last' | undefined

export function useRovingTabIndex(): {containerRef: React.RefObject<HTMLElement>} {
  const {containerRef} = useFocusZone({
    bindKeys: FocusKeys.ArrowVertical | FocusKeys.HomeAndEnd | FocusKeys.PageUpDown | FocusKeys.Tab,
    focusInStrategy: 'previous',
    focusableElementFilter: element => {
      if (element.getAttribute('role') === 'list') {
        return false
      }

      // Filters out elements that are hidden, display none, and anchors missing href
      // can cause performance loss
      // TODO: remove and set useFocusZone with `strict:true` once
      // https://github.com/primer/behaviors/pull/235 is merged
      const style = getComputedStyle(element)
      if (style.display === 'none' || style.visibility === 'hidden') {
        return false
      }

      return true
    },
    getNextFocusable: (direction, from, event) => {
      if (!(from instanceof HTMLElement)) return

      // eslint-disable-next-line no-restricted-syntax
      if (event.key === 'Tab' || event.code === 'Tab') {
        // Custom tab focus behavior:
        // 1. Check if focus is on the list item
        // 2. Find the previous focusable element before the listview.
        // 3. Move focus to that.
        if (event.shiftKey && from.getAttribute('role') === 'listitem') {
          return getFocusElementOutsideOfListView(from, 'previous')
        }

        // Custom tab focus behavior:
        // 1. Check if focus is on the last focusable item in a list item
        // 2. Find the next focusable element after the listview.
        // 3. Move focus to that.
        const relativeFocusPosition = getRelativeFocusPositionInListItem(from, event)
        if (!event.shiftKey && relativeFocusPosition === 'last') {
          return getFocusElementOutsideOfListView(from, 'next')
        }

        // If undefined is returned, the regular algorithm to select the next element to focus will be used.
        // https://primer.style/react/focusZone#focuszonesettings-interface
        return
      }

      if (direction === 'previous') {
        // Focus previous visible element
        return getListItemElement(from, direction) || getFirstElement(from)
      }

      if (direction === 'next') {
        // Focus next visible element
        return getListItemElement(from, direction) || getLastElement(from)
      }

      if (direction === 'start') {
        return getFirstElement(from)
      }

      if (direction === 'end') {
        return getLastElement(from)
      }

      return undefined
    },
  })
  return {containerRef}
}

function getListItemElement(element: HTMLElement, direction: 'next' | 'previous'): HTMLElement | undefined {
  let listItem = element

  // In case the event is fired from a child element, find the parent listitem
  const foundListItem: HTMLElement | null = element.closest('[role=listitem]')
  if (foundListItem) listItem = foundListItem

  const root = listItem.closest('[role=list]')
  if (!root) return

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT, node => {
    if (!(node instanceof HTMLElement)) return NodeFilter.FILTER_SKIP
    return node.getAttribute('role') === 'listitem' ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP
  })

  let current = walker.firstChild()

  let i = 0
  while (current !== listItem) {
    current = walker.nextNode()
    i++
    if (i > 500) break // fail-safe to not cause an endless loop
  }

  let next = direction === 'next' ? walker.nextNode() : walker.previousNode()

  // If next element is nested inside a collapsed sublist, continue iterating
  while (next instanceof HTMLElement && next.parentElement?.closest('[role=listitem][aria-expanded=false]')) {
    next = direction === 'next' ? walker.nextNode() : walker.previousNode()
  }

  return next instanceof HTMLElement ? next : undefined
}

function getFirstElement(element: HTMLElement): HTMLElement | undefined {
  const root = element.closest('[role=list]')
  const first = root?.querySelector('[role=listitem]')
  return first instanceof HTMLElement ? first : undefined
}

function getLastElement(element: HTMLElement): HTMLElement | undefined {
  const root = element.closest('[role=list]')
  const items = root?.querySelectorAll('[role=listitem]')

  if (!items || items.length < 1) return

  const last = items[items.length - 1]
  if (last instanceof HTMLElement) return last
}

function getRelativeFocusPositionInListItem(element: HTMLElement, event: KeyboardEvent): RelativeElementPosition {
  const listItem = element.closest('[role=listitem]') as HTMLElement
  if (!listItem) return

  const focusableElements = [...iterateFocusableElements(listItem, {strict: true})]
  const eventIndex = focusableElements.indexOf(event.target as HTMLElement)
  if (eventIndex === focusableElements.length - 1) {
    return 'last'
  }

  return
}

function getFocusElementOutsideOfListView(
  element: HTMLElement,
  direction: 'previous' | 'next',
): HTMLElement | undefined {
  // Find all focusable elements in the document excluding elements in ListView.
  // All focusable elements inside a listview are programatically given a tabindex of -1 because of the focusZone hook
  // so by setting onlyTabbable:true we disregard elements in ListView to find the next focusable element outside of listview
  const focusableElements = [...iterateFocusableElements(document.documentElement, {strict: true, onlyTabbable: true})]
  const currentFocusedElementIndex = focusableElements.indexOf(element)

  // Search for the nearest focusable element before/after the current focused element
  // that is outside of a list view and is focusable.
  return direction === 'next'
    ? focusableElements[currentFocusedElementIndex + 1]
    : focusableElements[currentFocusedElementIndex - 1]
}
